import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './styles/App.css';
import './styles/locations.css';
import './styles/forms.css';
import './styles/activity.css';
import './styles/point-history.css';
import './i18n/i18n';
import Skeleton from './components/Skeleton';

// Keep essential components as regular imports
import Navbar from './components/Navbar';
import PWAInstallBanner from './components/PWAInstallBanner';
import OfflineBanner from './components/OfflineBanner';
import UpdatePrompt from './components/UpdatePrompt';
import { usersAPI, authAPI } from './services/api';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import { ThemeProvider } from './contexts/ThemeContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { useRoutePreloader } from './hooks/useRoutePreloader';
import { config } from './utils/config';
import { identifyUser } from './utils/analytics';

// Lazy load all page components for code splitting
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const EmailVerification = lazy(() => import('./pages/EmailVerification'));
const Home = lazy(() => import('./pages/Home'));
const Locations = lazy(() => import('./pages/Locations'));
const LocationDetail = lazy(() => import('./pages/LocationDetail'));
const Events = lazy(() => import('./pages/Events'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const Challenges = lazy(() => import('./pages/Challenges'));
const ChallengeDetail = lazy(() => import('./pages/ChallengeDetail'));
const CreateChallenge = lazy(() => import('./pages/CreateChallenge'));
const EditChallenge = lazy(() => import('./pages/EditChallenge'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const Users = lazy(() => import('./pages/Users'));
const Calendar = lazy(() => import('./pages/Calendar'));
const CreateEvent = lazy(() => import('./pages/CreateEvent'));
const EventWishlist = lazy(() => import('./pages/EventWishlist'));
const Statistics = lazy(() => import('./pages/Statistics'));
const Settings = lazy(() => import('./pages/Settings'));
const Support = lazy(() => import('./pages/Support'));
const Notifications = lazy(() => import('./pages/Notifications'));
const MediaGallery = lazy(() => import('./pages/MediaGallery'));
const Badges = lazy(() => import('./pages/Badges'));
const PointHistory = lazy(() => import('./pages/PointHistory'));
const HallOfFame = lazy(() => import('./pages/HallOfFame'));

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waitingWorker, setWaitingWorker] = useState(null);
  const { i18n } = useTranslation();

  // Enable intelligent route preloading
  useRoutePreloader(true);

  // Wrapper to set user and identify in analytics
  const handleSetUser = (userData) => {
    setUser(userData);

    // Identify user in Umami analytics
    if (userData?.username) {
      identifyUser(userData.username, {
        userId: userData.id,
        email: userData.email,
        isAdmin: userData.is_admin
      });
    }
  };

  // Expose service worker update callback to window
  useEffect(() => {
    window.onUpdateAvailable = (registration) => {
      setWaitingWorker(registration);
    };
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Cookie is sent automatically with the request
      const response = await usersAPI.getMe();
      handleSetUser(response.data);
    } catch (error) {
      // Session invalid or expired
      setUser(null);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <Skeleton width="120px" height="120px" borderRadius="50%" />
        <Skeleton width="200px" height="24px" />
        <Skeleton width="150px" height="16px" />
      </div>
    );
  }

  return (
    <ThemeProvider>
      <ToastProvider>
        <ConfirmProvider>
          <WebSocketProvider user={user}>
            <Router>
              <div className="App">
                  {/* Offline Status Banner */}
                  <OfflineBanner />

                  {/* Update Available Prompt */}
                  <UpdatePrompt registration={waitingWorker} />

              {user && (
                <Navbar
                  user={user}
                  onLogout={handleLogout}
                  onChangeLanguage={changeLanguage}
                  currentLanguage={i18n.language}
                />
              )}
              <Suspense fallback={<PageLoader />}>
                <Routes>
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleSetUser} /> : <Navigate to="/" />}
          />
          {config.registrationEnabled && (
            <Route
              path="/register"
              element={!user ? <Register onRegister={handleSetUser} /> : <Navigate to="/" />}
            />
          )}
          <Route
            path="/forgot-password"
            element={!user ? <ForgotPassword /> : <Navigate to="/" />}
          />
          <Route
            path="/reset-password"
            element={!user ? <ResetPassword /> : <Navigate to="/" />}
          />
          <Route
            path="/verify-email"
            element={<EmailVerification />}
          />
          <Route
            path="/"
            element={user ? <Home user={user} setUser={setUser} /> : <Navigate to="/login" />}
          />
          <Route
            path="/locations"
            element={user ? <Locations user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/locations/:id"
            element={user ? <LocationDetail user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/events"
            element={user ? <Events user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/events/new"
            element={user ? <CreateEvent user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/events/:id"
            element={user ? <EventDetail user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/wishlist"
            element={user ? <EventWishlist user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/challenges"
            element={user ? <Challenges user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/challenges/create"
            element={user ? <CreateChallenge user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/challenges/:id/edit"
            element={user ? <EditChallenge user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/challenges/:id"
            element={user ? <ChallengeDetail user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/profile"
            element={user ? <UserProfile user={user} setUser={setUser} /> : <Navigate to="/login" />}
          />
          <Route
            path="/leaderboard"
            element={user ? <Leaderboard /> : <Navigate to="/login" />}
          />
          <Route
            path="/badges"
            element={user ? <Badges user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/point-history"
            element={user ? <PointHistory user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/hall-of-fame"
            element={user ? <HallOfFame /> : <Navigate to="/login" />}
          />
          <Route
            path="/users"
            element={user ? <Users currentUser={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/users/:userId"
            element={user ? <UserProfile user={user} setUser={setUser} /> : <Navigate to="/login" />}
          />
          <Route
            path="/calendar"
            element={user ? <Calendar /> : <Navigate to="/login" />}
          />
          <Route
            path="/statistics"
            element={user ? <Statistics /> : <Navigate to="/login" />}
          />
          <Route
            path="/settings"
            element={user ? <Settings /> : <Navigate to="/login" />}
          />
          <Route
            path="/support"
            element={user ? <Support /> : <Navigate to="/login" />}
          />
          <Route
            path="/notifications"
            element={user ? <Notifications /> : <Navigate to="/login" />}
          />
          <Route
            path="/media"
            element={user ? <MediaGallery user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin"
            element={
              user && user.is_admin ? <AdminPanel user={user} /> : <Navigate to="/" />
            }
          />
              </Routes>
              </Suspense>

              {/* PWA Install Banner */}
              <PWAInstallBanner />
            </div>
            </Router>
        </WebSocketProvider>
        </ConfirmProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="page-loader">
    <div className="page-loader-content">
      <Skeleton width="100%" height="60px" borderRadius="0.75rem" />
      <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
        <Skeleton width="200px" height="40px" borderRadius="0.5rem" />
        <Skeleton width="150px" height="40px" borderRadius="0.5rem" />
      </div>
      <div style={{ marginTop: '2rem' }}>
        <Skeleton width="100%" height="300px" borderRadius="0.75rem" />
      </div>
    </div>
  </div>
);

export default App;
